import React, { useState } from "react";
import {
  Box,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDeleteNoteMutation, useGetNotesQuery } from "../../../../../app/notesApiSlice";
import NotePreviewContent from "./NotePreviewContent";

const ShareNotes = () => {
  const [sortBy, setSortBy] = useState("topic");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const [openDeleteModal, setOpenDeleteModal] = useState({});
  const [openPreviewModals, setOpenPreviewModals] = useState({});
  const [deletedModals, setDeletedModals] = useState({});

  const { data, isLoading } = useGetNotesQuery();
  const [deleteNote, { isLoading: isDeletingNote }] = useDeleteNoteMutation();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const sortedNotes = React.useMemo(() => {
    if (!data?.notes) return [];
    return [...data.notes].sort((a, b) => {
      if (sortBy === "level") {
        return a.level.localeCompare(b.level);
      } else if (sortBy === "subject") {
        return a.subject.localeCompare(b.subject);
      } else if (sortBy === "topic") {
        return a.topic.localeCompare(b.topic);
      }
      return 0;
    });
  }, [data, sortBy]);

  const paginatedNotes = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedNotes.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedNotes, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const totalNotes = sortedNotes.length;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalNotes);

  const handleOpenDeleteModal = (noteId) => {
    setOpenDeleteModal((prev) => ({ ...prev, [noteId]: true }));
  };

  const handleOpenPreviewModal = (noteId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [noteId]: true }));
  };

  const handleCloseDeleteModal = (noteId) => {
    setOpenDeleteModal((prev) => ({ ...prev, [noteId]: false }));
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteNote({ id: noteId }).unwrap();
      handleCloseDeleteModal(noteId);
      setDeletedModals((prev) => ({ ...prev, [noteId]: true }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseDeletedModal = (noteId) => {
    setDeletedModals((prev) => ({ ...prev, [noteId]: false }));
  };

  const handleClosePreviewModal = (noteId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [noteId]: false }));
  };

  return (
    <Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Select value={sortBy} onChange={handleChangeSort} size="small">
            <MenuItem value="topic">Sort by: Topic</MenuItem>
            <MenuItem value="subject">Sort by: Subject</MenuItem>
            <MenuItem value="level">Sort by: Level</MenuItem>
          </Select>
        </Box>

        {isLoading && <LinearProgress />}

        {paginatedNotes.length === 0 && !isLoading && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">No notes found</Typography>
          </Box>
        )}

        {paginatedNotes.map((note) => (
          <Card key={note.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  {note.topic}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatDate(note.updated_at)}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Subject:</strong> {note.subject}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <strong>Level:</strong> {note.level}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: "100px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  "& p": { margin: 0 },
                  "& h1, & h2, & h3, & h4, & h5, & h6": { marginTop: 0, marginBottom: "0.5em", fontSize: "1em" },
                }}
                dangerouslySetInnerHTML={{ __html: note.body }}
              />
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button
                size="small"
                onClick={() => handleOpenPreviewModal(note.id)}
              >
                Preview
              </Button>
              <Button
                size="small"
                onClick={() =>
                  navigate(`/lms/notes/share-note/${note.id}`, {
                    state: { note },
                  })
                }
              >
                Share
              </Button>
              <Button
                size="small"
                onClick={() => handleOpenDeleteModal(note.id)}
                color="error"
              >
                Delete
              </Button>
            </CardActions>

            {/* Delete Modal */}
            <Dialog
              open={openDeleteModal[note.id] || false}
              onClose={() => handleCloseDeleteModal(note.id)}
            >
              <DialogTitle>Delete Note</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this note? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleCloseDeleteModal(note.id)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(note.id)}
                  color="error"
                  disabled={isDeletingNote}
                >
                  {isDeletingNote ? "Deleting..." : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            <Dialog
              open={openPreviewModals[note.id] || false}
              onClose={() => handleClosePreviewModal(note.id)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>{note.topic}</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Subject: {note.subject}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Level: {note.level}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatDate(note.updated_at)}
                  </Typography>
                </Box>
                <NotePreviewContent note={note} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleClosePreviewModal(note.id)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Success Modal */}
            <Dialog
              open={deletedModals[note.id] || false}
              onClose={() => handleCloseDeletedModal(note.id)}
            >
              <DialogTitle>Success</DialogTitle>
              <DialogContent>
                <Typography>Note deleted successfully.</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleCloseDeletedModal(note.id)}>
                  OK
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
        ))}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalNotes} entries
          </Typography>

          <Pagination
            count={Math.ceil(sortedNotes.length / itemsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ShareNotes;
